import {
  cacheDesignMode,
  DEFAULT_DESIGN_MODE,
  isTestNodeEnv,
  paintedDesignMode,
  type DesignMode
} from 'constants/config';
import { reportError } from 'utils/sentry.utils';

export const DESIGN_MODE_RELOAD_LATCH_KEY = 'ENV0_DESIGN_MODE_RELOADED';

export const DESIGN_SWITCHED_BACK_KEY = 'ENV0_DESIGN_SWITCHED_BACK';

export const markSwitchedBackToClassic = () => sessionStorage.setItem(DESIGN_SWITCHED_BACK_KEY, 'true');

export const popSwitchedBackToClassic = (): boolean => {
  const marked = sessionStorage.getItem(DESIGN_SWITCHED_BACK_KEY) === 'true';
  sessionStorage.removeItem(DESIGN_SWITCHED_BACK_KEY);

  return marked;
};

export const resolveDesignMode = ({
  isDesignSwitchAvailable,
  storedDesign,
  isTestEnv = isTestNodeEnv
}: {
  isDesignSwitchAvailable: boolean;
  storedDesign?: DesignMode;
  isTestEnv?: boolean;
}): DesignMode => {
  if (isTestEnv) return DEFAULT_DESIGN_MODE;
  if (!isDesignSwitchAvailable) return 'legacy';

  return storedDesign ?? DEFAULT_DESIGN_MODE;
};

/**
 * The corrective reload is only cheap while nothing is on screen to lose - left to fire whenever the
 * preference resolves it lands mid-flow, which for a new user is the middle of onboarding.
 *
 * Only a load without a cached choice can still need it, and only one with a session: flags are never
 * evaluated on the login screens, so waiting there would hold the login form back for nothing.
 */
export const shouldHoldFirstPaint = ({
  hadNoCache,
  hasSession,
  isResolved,
  hasWaitedLongEnough
}: {
  hadNoCache: boolean;
  hasSession: boolean;
  isResolved: boolean;
  hasWaitedLongEnough: boolean;
}): boolean => hadNoCache && hasSession && !isResolved && !hasWaitedLongEnough;

export const applyDesignMode = (mode: DesignMode, painted: DesignMode = paintedDesignMode): boolean => {
  cacheDesignMode(mode);

  if (mode === painted) {
    sessionStorage.removeItem(DESIGN_MODE_RELOAD_LATCH_KEY);
    return false;
  }

  if (sessionStorage.getItem(DESIGN_MODE_RELOAD_LATCH_KEY) === mode) {
    reportError('Design mode reload did not take effect', new Error('Design mode mismatch survived a reload'), {
      painted,
      resolved: mode
    });
    return false;
  }

  sessionStorage.setItem(DESIGN_MODE_RELOAD_LATCH_KEY, mode);
  window.location.reload();

  return true;
};
